<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import BackgroundSection from '@/components/imockup/inspector/BackgroundSection.vue'
import ExportSection from '@/components/imockup/inspector/ExportSection.vue'
import MockupControlsSection from '@/components/imockup/inspector/MockupControlsSection.vue'
import ScreenMediaOverwriteDialog from '@/components/imockup/ScreenMediaOverwriteDialog.vue'
import ProjectSection from '@/components/imockup/inspector/ProjectSection.vue'
import SceneSettingsSection from '@/components/imockup/inspector/SceneSettingsSection.vue'
import type { InspectorTabId } from '@/stores/mockupEditor'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const { inspectorTab } = storeToRefs(store)

const tabs = computed<{ id: InspectorTabId; label: string }[]>(() => [
  { id: 'basic', label: store.tUi('inspectorPanel.tabBasic') },
  { id: 'controls', label: store.tUi('inspectorPanel.tabControls') },
])

function setTab(id: InspectorTabId): void {
  store.setInspectorTab(id)
}
</script>

<template>
  <aside class="rail" :aria-label="store.tUi('inspectorPanel.inspectorAria')">
    <ScreenMediaOverwriteDialog />
    <div class="tabs" role="tablist" :aria-label="store.tUi('inspectorPanel.tabsAria')">
      <button
        v-for="t in tabs"
        :key="t.id"
        type="button"
        class="tab"
        role="tab"
        :aria-selected="inspectorTab === t.id ? 'true' : 'false'"
        :data-active="inspectorTab === t.id ? 'true' : 'false'"
        @click="setTab(t.id)"
      >
        {{ t.label }}
      </button>
    </div>

    <div class="scroll">
      <div v-show="inspectorTab === 'basic'" class="stack" role="tabpanel">
        <SceneSettingsSection />
        <hr class="hr" />
        <BackgroundSection />
        <hr class="hr" />
        <ExportSection />
        <hr class="hr" />
        <ProjectSection />
      </div>

      <div v-show="inspectorTab === 'controls'" class="stack" role="tabpanel">
        <MockupControlsSection />
      </div>
    </div>
  </aside>
</template>

<style scoped>
.rail {
  width: var(--panel-right, var(--im-inspector-w));
  max-width: 100%;
  border-left: 1px solid var(--im-border);
  background: var(--im-surface);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem;
  padding: 0.55rem 0.55rem 0.5rem;
  border-bottom: 1px solid var(--im-border);
  background: var(--im-surface);
}

.tab {
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--im-muted);
  font-weight: 850;
  font-size: 0.8125rem;
  padding: 0.5rem 0.45rem;
  cursor: pointer;
  transition:
    background 160ms ease,
    color 160ms ease,
    border-color 160ms ease;
}

.tab:hover {
  color: var(--im-text);
  background: var(--im-interactive-soft);
}

.tab:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 2px;
}

.tab[data-active='true'] {
  color: var(--im-interactive-fg);
  border-color: var(--im-interactive-bg);
  background: var(--im-interactive-bg);
}

.scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0.75rem 0.65rem 0.9rem;
}

.stack {
  display: grid;
  gap: 0.85rem;
}

.hr {
  border: 0;
  border-top: 1px solid var(--im-border);
  margin: 0;
}

@media (max-width: 900px) {
  .rail {
    width: 100%;
    border-left: 0;
    border-top: 1px solid var(--im-border);
    max-height: min(48vh, 560px);
  }
}
</style>
